import { friendInvitePath } from "./friends";

export const PUSH_CATEGORIES = [
  "votingReminders",
  "showStarted",
  "firstWinner",
  "adminMessages",
  "partyInvites",
] as const;

export type PushCategory = (typeof PUSH_CATEGORIES)[number];

export const PUSH_KINDS = [
  "voting_reminder_t24",
  "voting_reminder_t3",
  "show_started",
  "red_carpet_on",
  "first_winner",
  "admin_custom",
  "friend_party_invite",
] as const;

export type PushKind = (typeof PUSH_KINDS)[number];

export const PUSH_PLATFORMS = ["web", "ios", "android"] as const;

export type PushPlatform = (typeof PUSH_PLATFORMS)[number];

export const PUSH_PREFERENCE_PRESETS = [
  "all",
  "votingReminders",
  "showUpdates",
  "off",
] as const;

export type PushPreferencePreset = (typeof PUSH_PREFERENCE_PRESETS)[number];

export interface PushPreferences {
  votingReminders: boolean;
  showStarted: boolean;
  firstWinner: boolean;
  adminMessages: boolean;
  partyInvites: boolean;
}

export const DEFAULT_PUSH_PREFERENCES: PushPreferences = {
  votingReminders: true,
  showStarted: true,
  firstWinner: true,
  adminMessages: true,
  partyInvites: true,
};

export const PUSH_TITLE_MAX = 80;
export const PUSH_BODY_MAX = 240;
export const PUSH_URL_MAX = 500;
export const SHOW_STARTED_WINDOW_MS = 3 * 60 * 60 * 1000;
export const FIRST_WINNER_WINDOW_MS = 3 * 60 * 60 * 1000;
/** Default lead before show start for the one-time red carpet push. */
export const RED_CARPET_PUSH_LEAD_MS = 90 * 60 * 1000;
const RED_CARPET_PUSH_LEAD_MIN_MS = 30 * 60 * 1000;
const RED_CARPET_PUSH_LEAD_MAX_MS = 180 * 60 * 1000;

export function resolveRedCarpetPushLeadMs(
  minutes: number | null | undefined,
): number {
  if (minutes == null || !Number.isFinite(minutes)) {
    return RED_CARPET_PUSH_LEAD_MS;
  }
  const leadMs = minutes * 60 * 1000;
  return Math.min(
    RED_CARPET_PUSH_LEAD_MAX_MS,
    Math.max(RED_CARPET_PUSH_LEAD_MIN_MS, leadMs),
  );
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EXPO_PUSH_TOKEN_PATTERN =
  /^(ExponentPushToken|ExpoPushToken)\[[A-Za-z0-9_-]+\]$/;

export type PushAudienceMatch = "any" | "all";

export type PushAudienceRole = "OWNER" | "MEMBER";

export interface PushAudience {
  everyone: boolean;
  match: PushAudienceMatch;
  userIds: string[];
  partyIds: string[];
  awardShowIds: string[];
  roles: PushAudienceRole[] | null;
}

export interface PushAudienceMembership {
  partyId: string;
  awardShowId: string;
  role: PushAudienceRole;
}

export interface PushAudienceMember {
  userId: string;
  memberships: PushAudienceMembership[];
}

export interface TemplatedPush {
  source: "template";
  kind: Exclude<PushKind, "admin_custom">;
  category: Exclude<PushCategory, "adminMessages">;
  dedupeKey: string;
  urlPath: string;
  titleKey: string;
  bodyKey: string;
  values: Record<string, string | number>;
  winnerNames?: string[];
}

export interface LiteralPush {
  source: "literal";
  kind: "admin_custom";
  category: "adminMessages";
  dedupeKey: string;
  urlPath: string;
  title: string;
  body: string;
}

export type PushDraft = TemplatedPush | LiteralPush;

export interface PlannedPush {
  userId: string;
  draft: TemplatedPush;
}

export interface PushSubscriptionRef {
  userId: string;
  disabledAt: string | Date | null;
}

export interface ShowStartedPushRow {
  awardShowId: string;
  awardShowName: string;
  startTime: Date;
  userId: string;
  partyId: string;
  partyName: string;
  role: PushAudienceRole;
  joinedAt: Date;
}

export interface FirstWinnerPushRow {
  awardShowId: string;
  awardShowName: string;
  announcedAt: Date;
  categoryName: string;
  winnerNames: string[];
  userId: string;
  partyId: string;
  partyName: string;
  role: PushAudienceRole;
  joinedAt: Date;
}

type TranslatePush = (
  key: string,
  values?: Record<string, string | number>,
) => string;

export function categoryForPushKind(kind: PushKind): PushCategory {
  switch (kind) {
    case "voting_reminder_t24":
    case "voting_reminder_t3":
      return "votingReminders";
    case "show_started":
    case "red_carpet_on":
      return "showStarted";
    case "first_winner":
      return "firstWinner";
    case "admin_custom":
      return "adminMessages";
    case "friend_party_invite":
      return "partyInvites";
  }
}

export function pushAllowed(
  preferences: PushPreferences | null | undefined,
  category: PushCategory,
): boolean {
  return (preferences ?? DEFAULT_PUSH_PREFERENCES)[category];
}

export function pushPreferencePreset(
  preset: PushPreferencePreset,
): PushPreferences {
  switch (preset) {
    case "all":
      return { ...DEFAULT_PUSH_PREFERENCES };
    case "votingReminders":
      return {
        votingReminders: true,
        showStarted: false,
        firstWinner: false,
        adminMessages: false,
        partyInvites: false,
      };
    case "showUpdates":
      return {
        votingReminders: false,
        showStarted: true,
        firstWinner: true,
        adminMessages: false,
        partyInvites: false,
      };
    case "off":
      return {
        votingReminders: false,
        showStarted: false,
        firstWinner: false,
        adminMessages: false,
        partyInvites: false,
      };
  }
}

export function matchingPushPreset(
  preferences: PushPreferences,
): PushPreferencePreset | "custom" {
  for (const preset of PUSH_PREFERENCE_PRESETS) {
    const candidate = pushPreferencePreset(preset);
    if (
      PUSH_CATEGORIES.every(
        (category) => candidate[category] === preferences[category],
      )
    ) {
      return preset;
    }
  }
  return "custom";
}

export function parsePushPreferences(
  value: unknown,
): { ok: true; value: PushPreferences } | { ok: false; error: "invalidPreferences" } {
  if (!value || typeof value !== "object") {
    return { ok: false, error: "invalidPreferences" };
  }
  const record = value as Record<string, unknown>;
  const preferences = {} as PushPreferences;
  for (const category of PUSH_CATEGORIES) {
    const setting = record[category];
    if (typeof setting !== "boolean") {
      return { ok: false, error: "invalidPreferences" };
    }
    preferences[category] = setting;
  }
  return { ok: true, value: preferences };
}

function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

function parseIdList(
  value: unknown,
): { ok: true; ids: string[] } | { ok: false } {
  if (value == null) {
    return { ok: true, ids: [] };
  }
  if (!Array.isArray(value)) {
    return { ok: false };
  }
  const ids: string[] = [];
  for (const entry of value) {
    if (typeof entry !== "string") {
      return { ok: false };
    }
    const trimmed = entry.trim();
    if (!trimmed) {
      continue;
    }
    if (!isUuid(trimmed)) {
      return { ok: false };
    }
    const normalized = trimmed.toLowerCase();
    if (!ids.includes(normalized)) {
      ids.push(normalized);
    }
  }
  return { ok: true, ids };
}

function parseRoles(
  value: unknown,
): { ok: true; roles: PushAudienceRole[] | null } | { ok: false } {
  if (value == null) {
    return { ok: true, roles: null };
  }
  if (!Array.isArray(value)) {
    return { ok: false };
  }
  const roles: PushAudienceRole[] = [];
  for (const entry of value) {
    if (entry !== "OWNER" && entry !== "MEMBER") {
      return { ok: false };
    }
    if (!roles.includes(entry)) {
      roles.push(entry);
    }
  }
  return { ok: true, roles: roles.length > 0 ? roles : null };
}

export function parsePushAudience(
  value: unknown,
):
  | { ok: true; value: PushAudience }
  | { ok: false; error: "invalidAudience" | "audienceEmpty" } {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ok: false, error: "invalidAudience" };
  }
  const record = value as Record<string, unknown>;
  if (
    record.everyone != null &&
    typeof record.everyone !== "boolean"
  ) {
    return { ok: false, error: "invalidAudience" };
  }
  const everyone = record.everyone === true;
  const match = record.match == null ? "any" : record.match;
  if (match !== "any" && match !== "all") {
    return { ok: false, error: "invalidAudience" };
  }
  const userIds = parseIdList(record.userIds);
  const partyIds = parseIdList(record.partyIds);
  const awardShowIds = parseIdList(record.awardShowIds);
  const roles = parseRoles(record.roles);
  if (!userIds.ok || !partyIds.ok || !awardShowIds.ok || !roles.ok) {
    return { ok: false, error: "invalidAudience" };
  }
  if (
    !everyone &&
    userIds.ids.length === 0 &&
    partyIds.ids.length === 0 &&
    awardShowIds.ids.length === 0
  ) {
    return { ok: false, error: "audienceEmpty" };
  }
  return {
    ok: true,
    value: {
      everyone,
      match,
      userIds: userIds.ids,
      partyIds: partyIds.ids,
      awardShowIds: awardShowIds.ids,
      roles: roles.roles,
    },
  };
}

function membershipMatches(
  membership: PushAudienceMembership,
  audience: PushAudience,
  dimension: "party" | "show",
): boolean {
  const inDimension =
    dimension === "party"
      ? audience.partyIds.includes(membership.partyId)
      : audience.awardShowIds.includes(membership.awardShowId);
  if (!inDimension) {
    return false;
  }
  if (!audience.roles) {
    return true;
  }
  return audience.roles.includes(membership.role);
}

export function userMatchesPushAudience(
  member: PushAudienceMember,
  audience: PushAudience,
): boolean {
  if (audience.everyone) {
    return true;
  }

  const userHit = audience.userIds.includes(member.userId);
  const partyHit =
    audience.partyIds.length > 0 &&
    member.memberships.some((membership) =>
      membershipMatches(membership, audience, "party"),
    );
  const showHit =
    audience.awardShowIds.length > 0 &&
    member.memberships.some((membership) =>
      membershipMatches(membership, audience, "show"),
    );

  if (audience.match === "all") {
    if (audience.userIds.length > 0 && !userHit) {
      return false;
    }
    if (audience.partyIds.length > 0 && !partyHit) {
      return false;
    }
    if (audience.awardShowIds.length > 0 && !showHit) {
      return false;
    }
    return (
      audience.userIds.length > 0 ||
      audience.partyIds.length > 0 ||
      audience.awardShowIds.length > 0
    );
  }

  return userHit || partyHit || showHit;
}

export function normalizePushUrlPath(value: unknown): string | null {
  if (value == null) {
    return "/";
  }
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return "/";
  }
  if (trimmed.includes("\\") || trimmed.includes("://") || trimmed.startsWith("//")) {
    return null;
  }
  if (/\s/.test(trimmed) || trimmed.includes("..")) {
    return null;
  }
  const withSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (withSlash.startsWith("//") || withSlash.length > PUSH_URL_MAX) {
    return null;
  }
  return withSlash;
}

export type AdminPushDraftError =
  | "titleRequired"
  | "titleTooLong"
  | "bodyRequired"
  | "bodyTooLong"
  | "invalidUrl"
  | "invalidAudience"
  | "audienceEmpty";

export function parseAdminPushDraft(input: {
  title: unknown;
  body: unknown;
  urlPath: unknown;
  audience: unknown;
}):
  | {
      ok: true;
      value: {
        title: string;
        body: string;
        urlPath: string;
        audience: PushAudience;
      };
    }
  | { ok: false; error: AdminPushDraftError } {
  if (typeof input.title !== "string" || input.title.trim().length === 0) {
    return { ok: false, error: "titleRequired" };
  }
  const title = input.title.trim();
  if (title.length > PUSH_TITLE_MAX) {
    return { ok: false, error: "titleTooLong" };
  }
  if (typeof input.body !== "string" || input.body.trim().length === 0) {
    return { ok: false, error: "bodyRequired" };
  }
  const body = input.body.trim();
  if (body.length > PUSH_BODY_MAX) {
    return { ok: false, error: "bodyTooLong" };
  }
  const urlPath = normalizePushUrlPath(input.urlPath);
  if (!urlPath) {
    return { ok: false, error: "invalidUrl" };
  }
  const audience = parsePushAudience(input.audience);
  if (!audience.ok) {
    return audience;
  }
  return { ok: true, value: { title, body, urlPath, audience: audience.value } };
}

export function isExpoPushToken(value: string): boolean {
  return EXPO_PUSH_TOKEN_PATTERN.test(value);
}

export function parseWebPushSubscription(
  value: unknown,
):
  | { ok: true; value: { endpoint: string; p256dh: string; auth: string } }
  | { ok: false; error: "invalidSubscription" } {
  if (!value || typeof value !== "object") {
    return { ok: false, error: "invalidSubscription" };
  }
  const record = value as Record<string, unknown>;
  const endpoint = record.endpoint;
  const keys =
    record.keys && typeof record.keys === "object"
      ? (record.keys as Record<string, unknown>)
      : null;
  const p256dh = keys?.p256dh ?? record.p256dh;
  const auth = keys?.auth ?? record.auth;
  if (
    typeof endpoint !== "string" ||
    !endpoint.startsWith("https://") ||
    typeof p256dh !== "string" ||
    p256dh.trim().length === 0 ||
    typeof auth !== "string" ||
    auth.trim().length === 0
  ) {
    return { ok: false, error: "invalidSubscription" };
  }
  return {
    ok: true,
    value: { endpoint, p256dh: p256dh.trim(), auth: auth.trim() },
  };
}

export function buildVotingReminderPush(input: {
  partyId: string;
  userId: string;
  partyName: string;
  awardShowName: string;
  reminderKind: "t24" | "t3";
  remainingCount: number;
}): TemplatedPush {
  return {
    source: "template",
    kind:
      input.reminderKind === "t24"
        ? "voting_reminder_t24"
        : "voting_reminder_t3",
    category: "votingReminders",
    dedupeKey: `voting-reminder:${input.partyId}:${input.userId}:${input.reminderKind}`,
    urlPath: `/parties/${input.partyId}`,
    titleKey:
      input.reminderKind === "t24"
        ? "votingReminderT24Title"
        : "votingReminderT3Title",
    bodyKey: "votingReminderBody",
    values: {
      partyName: input.partyName,
      awardShowName: input.awardShowName,
      remainingCount: input.remainingCount,
    },
  };
}

export function buildShowStartedPush(input: {
  awardShowId: string;
  userId: string;
  awardShowName: string;
  partyId: string;
  partyName: string;
}): TemplatedPush {
  return {
    source: "template",
    kind: "show_started",
    category: "showStarted",
    dedupeKey: `show-started:${input.awardShowId}:${input.userId}`,
    urlPath: `/parties/${input.partyId}`,
    titleKey: "showStartedTitle",
    bodyKey: "showStartedBody",
    values: {
      awardShowName: input.awardShowName,
      partyName: input.partyName,
    },
  };
}

export function buildRedCarpetPush(input: {
  awardShowId: string;
  userId: string;
  awardShowName: string;
  partyId: string;
  partyName: string;
}): TemplatedPush {
  return {
    source: "template",
    kind: "red_carpet_on",
    category: "showStarted",
    dedupeKey: `red-carpet:${input.awardShowId}:${input.userId}`,
    urlPath: `/award-shows/${input.awardShowId}?tab=red-carpet&src=push_red_carpet`,
    titleKey: "redCarpetOnTitle",
    bodyKey: "redCarpetOnBody",
    values: {
      awardShowName: input.awardShowName,
      partyName: input.partyName,
    },
  };
}

export function buildFirstWinnerPush(input: {
  awardShowId: string;
  userId: string;
  awardShowName: string;
  categoryName: string;
  winnerNames: string[];
  partyId: string;
  partyName: string;
}): TemplatedPush {
  return {
    source: "template",
    kind: "first_winner",
    category: "firstWinner",
    dedupeKey: `first-winner:${input.awardShowId}:${input.userId}`,
    urlPath: `/parties/${input.partyId}`,
    titleKey: "firstWinnerTitle",
    bodyKey: "firstWinnerBody",
    values: {
      awardShowName: input.awardShowName,
      categoryName: input.categoryName,
      partyName: input.partyName,
    },
    winnerNames: input.winnerNames,
  };
}

export function buildFriendPartyInvitePush(input: {
  inviteId: string;
  invitedAt: string;
  inviterName: string;
  partyName: string;
  awardShowName: string;
}): TemplatedPush {
  return {
    source: "template",
    kind: "friend_party_invite",
    category: "partyInvites",
    dedupeKey: `friend-party-invite:${input.inviteId}:${input.invitedAt}`,
    urlPath: friendInvitePath(input.inviteId),
    titleKey: input.inviterName.trim()
      ? "friendPartyInviteTitle"
      : "friendPartyInviteTitleAnonymous",
    bodyKey: "friendPartyInviteBody",
    values: {
      inviterName: input.inviterName,
      partyName: input.partyName,
      awardShowName: input.awardShowName,
    },
  };
}

export function buildAdminPush(input: {
  campaignId: string;
  userId: string;
  title: string;
  body: string;
  urlPath: string;
}): LiteralPush {
  return {
    source: "literal",
    kind: "admin_custom",
    category: "adminMessages",
    dedupeKey: `admin:${input.campaignId}:${input.userId}`,
    urlPath: input.urlPath,
    title: input.title,
    body: input.body,
  };
}

function withinWindow(at: Date, now: Date, windowMs: number): boolean {
  const elapsed = now.getTime() - at.getTime();
  return elapsed >= 0 && elapsed <= windowMs;
}

function preferredParty<T extends { role: PushAudienceRole; joinedAt: Date }>(
  rows: T[],
): T {
  return [...rows].sort((left, right) => {
    if (left.role !== right.role) {
      return left.role === "OWNER" ? -1 : 1;
    }
    return left.joinedAt.getTime() - right.joinedAt.getTime();
  })[0]!;
}

export interface FirstWinnerFact {
  awardShowId: string;
  awardShowName: string;
  categoryId: string;
  categoryName: string;
  orderIndex: number;
  nomineeName: string;
  announcedAt: Date;
}

export interface FirstWinnerSelection {
  awardShowId: string;
  awardShowName: string;
  announcedAt: Date;
  categoryId: string;
  categoryName: string;
  winnerNames: string[];
}

export function selectFirstWinnerFacts(
  facts: readonly FirstWinnerFact[],
  now: Date,
  windowMs: number = FIRST_WINNER_WINDOW_MS,
): FirstWinnerSelection[] {
  const byShow = new Map<string, FirstWinnerFact[]>();
  for (const fact of facts) {
    const existing = byShow.get(fact.awardShowId);
    if (existing) {
      existing.push(fact);
    } else {
      byShow.set(fact.awardShowId, [fact]);
    }
  }

  const selections: FirstWinnerSelection[] = [];
  for (const group of byShow.values()) {
    const firstAt = group.reduce(
      (earliest, fact) =>
        fact.announcedAt.getTime() < earliest ? fact.announcedAt.getTime() : earliest,
      Number.POSITIVE_INFINITY,
    );
    if (!withinWindow(new Date(firstAt), now, windowMs)) {
      continue;
    }
    const firstWave = group.filter(
      (fact) => fact.announcedAt.getTime() === firstAt,
    );
    const category = [...firstWave].sort(
      (left, right) => left.orderIndex - right.orderIndex,
    )[0];
    if (!category) {
      continue;
    }
    const winnerNames = [
      ...new Set(
        firstWave
          .filter((fact) => fact.categoryId === category.categoryId)
          .map((fact) => fact.nomineeName.trim())
          .filter((name) => name.length > 0),
      ),
    ];
    if (winnerNames.length === 0) {
      continue;
    }
    selections.push({
      awardShowId: category.awardShowId,
      awardShowName: category.awardShowName,
      announcedAt: category.announcedAt,
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      winnerNames,
    });
  }
  return selections;
}

export function planShowStartedPushes(
  rows: readonly ShowStartedPushRow[],
  now: Date,
  alreadySentKeys: ReadonlySet<string> = new Set(),
  windowMs: number = SHOW_STARTED_WINDOW_MS,
): PlannedPush[] {
  const grouped = new Map<string, ShowStartedPushRow[]>();
  for (const row of rows) {
    if (!withinWindow(row.startTime, now, windowMs)) {
      continue;
    }
    const key = `${row.awardShowId}:${row.userId}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.push(row);
    } else {
      grouped.set(key, [row]);
    }
  }

  const planned: PlannedPush[] = [];
  for (const group of grouped.values()) {
    const row = preferredParty(group);
    const draft = buildShowStartedPush(row);
    if (alreadySentKeys.has(draft.dedupeKey)) {
      continue;
    }
    planned.push({ userId: row.userId, draft });
  }
  return planned;
}

function beforeStartWithinLead(
  startTime: Date,
  now: Date,
  leadMs: number,
): boolean {
  const remaining = startTime.getTime() - now.getTime();
  return remaining > 0 && remaining <= leadMs;
}

export function planRedCarpetPushes(
  rows: readonly ShowStartedPushRow[],
  now: Date,
  alreadySentKeys: ReadonlySet<string> = new Set(),
  leadMs: number = RED_CARPET_PUSH_LEAD_MS,
): PlannedPush[] {
  const grouped = new Map<string, ShowStartedPushRow[]>();
  for (const row of rows) {
    if (!beforeStartWithinLead(row.startTime, now, leadMs)) {
      continue;
    }
    const key = `${row.awardShowId}:${row.userId}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.push(row);
    } else {
      grouped.set(key, [row]);
    }
  }

  const planned: PlannedPush[] = [];
  for (const group of grouped.values()) {
    const row = preferredParty(group);
    const draft = buildRedCarpetPush(row);
    if (alreadySentKeys.has(draft.dedupeKey)) {
      continue;
    }
    planned.push({ userId: row.userId, draft });
  }
  return planned;
}

export function planFirstWinnerPushes(
  rows: readonly FirstWinnerPushRow[],
  now: Date,
  alreadySentKeys: ReadonlySet<string> = new Set(),
  windowMs: number = FIRST_WINNER_WINDOW_MS,
): PlannedPush[] {
  const grouped = new Map<string, FirstWinnerPushRow[]>();
  for (const row of rows) {
    if (row.winnerNames.length === 0) {
      continue;
    }
    if (!withinWindow(row.announcedAt, now, windowMs)) {
      continue;
    }
    const key = `${row.awardShowId}:${row.userId}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.push(row);
    } else {
      grouped.set(key, [row]);
    }
  }

  const planned: PlannedPush[] = [];
  for (const group of grouped.values()) {
    const row = preferredParty(group);
    const draft = buildFirstWinnerPush(row);
    if (alreadySentKeys.has(draft.dedupeKey)) {
      continue;
    }
    planned.push({ userId: row.userId, draft });
  }
  return planned;
}

export function renderPushDraft(
  draft: PushDraft,
  translate: TranslatePush,
  absoluteUrl: (path: string) => string,
  formatList: (names: string[]) => string,
): { title: string; body: string; url: string } {
  const url = absoluteUrl(draft.urlPath);
  if (draft.source === "literal") {
    return { title: draft.title, body: draft.body, url };
  }
  const values = { ...draft.values };
  if (draft.winnerNames && draft.winnerNames.length > 0) {
    values.winnerName = formatList(draft.winnerNames);
  }
  return {
    title: translate(draft.titleKey, values),
    body: translate(draft.bodyKey, values),
    url,
  };
}

export function filterSubscriptionsForCategory<T extends PushSubscriptionRef>(
  subscriptions: readonly T[],
  preferencesByUserId: ReadonlyMap<string, PushPreferences>,
  category: PushCategory,
): T[] {
  return subscriptions.filter((subscription) => {
    if (subscription.disabledAt) {
      return false;
    }
    return pushAllowed(preferencesByUserId.get(subscription.userId), category);
  });
}
